import React, { useState, useEffect } from 'react';
import {
    Plus,
    Search,
    Filter,
    MoreVertical,
    Eye,
    Edit,
    Trash2,
    MapPin,
    Tag,
    Package,
    ChevronLeft,
    Shirt,
    Shield,
    Users,
    AlertTriangle,
    LayoutGrid,
    Loader2,
    XCircle,
    Archive,
    RotateCcw,
    Clock,
    CheckCircle2,
    ArrowRightLeft,
    RefreshCcw,
    Minus
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Can } from '../../components/Can';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../../components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
    Dialog,
    DialogContent
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { cn } from '../../components/ui/utils';
import { AddItemModal, PreviewItemModal, DeleteConfirmModal, StockCyclingModal, ICON_MAP, COLOR_MAP } from './InventoryList';
import AddSupplierModal from './AddSupplierModal';
import { toast } from 'sonner';

interface ManageItemsProps {
    onBack: () => void;
}

const ManageItems: React.FC<ManageItemsProps> = ({ onBack }) => {
    const [items, setItems] = useState<any[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [locations, setLocations] = useState<any[]>([]);
    const [suppliers, setSuppliers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('all');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('Active');

    // Modal states
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isAddSupplierOpen, setIsAddSupplierOpen] = useState(false);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [isCycleModalOpen, setIsCycleModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<any>(null);

    useEffect(() => {
        fetchMeta();
    }, []);

    useEffect(() => {
        fetchData();
    }, [search, selectedLocation, selectedCategory, selectedStatus]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const query = new URLSearchParams();
            if (search) query.append('q', search);
            if (selectedLocation !== 'all') query.append('locationId', selectedLocation);
            if (selectedCategory !== 'all') query.append('categoryId', selectedCategory);
            if (selectedStatus !== 'all') query.append('status', selectedStatus);

            const data = await api.get(`/api/inventory/items?${query.toString()}`);
            setItems(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchMeta = async () => {
        try {
            const [cats, locs, sups] = await Promise.all([
                api.get('/api/inventory/categories'),
                api.get('/api/inventory/locations'),
                api.get('/api/inventory/suppliers')
            ]);
            setCategories(cats);
            setLocations(locs);
            setSuppliers(sups);
        } catch (err) {
            console.error(err);
        }
    };

    const handleRestoreItem = async (item: any) => {
        try {
            await api.post(`/api/inventory/items/${item.id}/restore`, {});
            toast.success('Item restored successfully');
            fetchData();
        } catch (err: any) {
            toast.error(err.message || 'Failed to restore item');
        }
    };

    const handleDeleteItem = async () => {
        if (!selectedItem) return;
        const linked = (selectedItem.issuedCount || 0) > 0 || (selectedItem.incomingCount || 0) > 0;
        try {
            if (linked && selectedItem.status !== 'Archived') {
                await api.post(`/api/inventory/items/${selectedItem.id}/archive`, {});
                toast.success('Item archived successfully');
            } else {
                await api.delete(`/api/inventory/items/${selectedItem.id}`);
                toast.success('Item deleted successfully');
            }
            fetchData();
            setIsDeleteModalOpen(false);
            setSelectedItem(null);
        } catch (err: any) {
            toast.error(err.message || `Failed to process request`);
        }
    };

    return (
        <div className="space-y-6 sm:space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onBack}
                        className="rounded-xl flex items-center gap-1.5 text-gray-400 hover:text-gray-900 transition-all p-0 h-auto hover:bg-transparent"
                    >
                        <ChevronLeft size={20} />
                        <span className="text-sm font-bold sm:inline hidden">Settings</span>
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold">Master Inventory</h1>
                        <p className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-widest mt-1.5 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                            Global Assets Control
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                    <Button
                        variant="outline"
                        onClick={() => setIsAddSupplierOpen(true)}
                        className="flex-1 sm:flex-initial rounded-2xl h-11 px-4 sm:px-6 border-gray-100 font-bold hover:bg-gray-50 transition-all shadow-sm text-xs sm:text-sm"
                    >
                        Add Supplier
                    </Button>
                    <Button
                        onClick={() => { setSelectedItem(null); setIsAddModalOpen(true); }}
                        className="flex-1 sm:flex-initial items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl h-11 px-4 sm:px-6 font-bold shadow-lg shadow-slate-900/10 transition-all text-xs sm:text-sm"
                    >
                        <Plus size={18} />
                        <span className="whitespace-nowrap">Add New Item</span>
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 sm:p-5 rounded-[32px] border border-gray-100 shadow-sm space-y-4">
                <div className="relative group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors" size={20} />
                    <Input
                        placeholder="Search master catalog by name or SKU..."
                        className="pl-12 rounded-[20px] border-gray-100 bg-gray-50/50 h-12 transition-all focus:bg-white focus:ring-4 focus:ring-blue-500/5 font-medium text-sm border-none shadow-inner"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="relative">
                        <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                            <SelectTrigger className="w-full rounded-[18px] border-gray-100 bg-gray-50/50 h-11 font-bold text-xs hover:bg-white transition-all">
                                <MapPin size={14} className="text-gray-400 mr-2" />
                                <SelectValue placeholder="All Locations" />
                            </SelectTrigger>
                            <SelectContent className="rounded-2xl border-gray-100">
                                <SelectItem value="all">All Locations</SelectItem>
                                {locations.map((loc) => (
                                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="relative">
                        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                            <SelectTrigger className="w-full rounded-[18px] border-gray-100 bg-gray-50/50 h-11 font-bold text-xs hover:bg-white transition-all">
                                <Tag size={14} className="text-gray-400 mr-2" />
                                <SelectValue placeholder="Category" />
                            </SelectTrigger>
                            <SelectContent className="rounded-2xl border-gray-100">
                                <SelectItem value="all">All Categories</SelectItem>
                                {categories.map((cat) => (
                                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="relative">
                        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                            <SelectTrigger className="w-full rounded-[18px] border-gray-100 bg-gray-50/50 h-11 font-bold text-xs hover:bg-white transition-all">
                                <Archive size={14} className="text-gray-400 mr-2" />
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent className="rounded-2xl border-gray-100">
                                <SelectItem value="Active">Active Only</SelectItem>
                                <SelectItem value="Archived">Archived Only</SelectItem>
                                <SelectItem value="all">All Statuses</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden">
                <Table>
                    <TableHeader className="bg-gray-50/50">
                        <TableRow className="border-b border-gray-100 hover:bg-transparent">
                            <TableHead className="w-[80px] font-bold text-gray-400 pl-6 uppercase text-[10px] tracking-wider">Photo</TableHead>
                            <TableHead className="font-bold text-gray-400 uppercase text-[10px] tracking-wider">Item Name</TableHead>
                            <TableHead className="font-bold text-gray-400 uppercase text-[10px] tracking-wider">Category</TableHead>
                            <TableHead className="font-bold text-gray-400 uppercase text-[10px] tracking-wider">Location</TableHead>
                            <TableHead className="font-bold text-gray-400 text-center uppercase text-[10px] tracking-wider">Stock</TableHead>
                            <TableHead className="font-bold text-gray-400 text-center uppercase text-[10px] tracking-wider">Issued</TableHead>
                            <TableHead className="font-bold text-gray-400 text-right uppercase text-[10px] tracking-wider">Cost</TableHead>
                            <TableHead className="w-[150px] text-right font-bold text-gray-400 pr-6 uppercase text-[10px] tracking-wider">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            [...Array(5)].map((_, i) => (
                                <TableRow key={i} className="animate-pulse">
                                    <TableCell className="pl-6"><div className="w-10 h-10 bg-gray-50 rounded-xl" /></TableCell>
                                    <TableCell><div className="w-32 h-4 bg-gray-50 rounded-full" /></TableCell>
                                    <TableCell><div className="w-20 h-4 bg-gray-50 rounded-full" /></TableCell>
                                    <TableCell><div className="w-24 h-4 bg-gray-50 rounded-full" /></TableCell>
                                    <TableCell><div className="w-8 h-4 mx-auto bg-gray-50 rounded-full" /></TableCell>
                                    <TableCell><div className="w-8 h-4 mx-auto bg-gray-50 rounded-full" /></TableCell>
                                    <TableCell><div className="w-16 h-4 ml-auto bg-gray-50 rounded-full" /></TableCell>
                                    <TableCell />
                                </TableRow>
                            ))
                        ) : items.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="h-96 text-center">
                                    <div className="flex flex-col items-center justify-center p-12">
                                        <div className="w-20 h-20 bg-gray-50 rounded-[32px] flex items-center justify-center mb-6">
                                            <Package size={32} className="text-gray-200" />
                                        </div>
                                        <h3 className="text-xl font-black text-gray-900 mb-2">Inventory is Empty</h3>
                                        <p className="text-gray-400 max-w-[280px] font-medium leading-relaxed">Your master catalog is ready. Start by adding your first inventory asset.</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            items.map((item) => {
                                const colorStyles = COLOR_MAP[item.category?.color || 'blue'] || COLOR_MAP.blue;
                                const IconComp = ICON_MAP[item.category?.icon as string] || Tag;
                                return (
                                    <TableRow key={item.id} className="group hover:bg-gray-50/50 transition-colors border-b border-gray-50 last:border-0">
                                        <TableCell className="pl-6 py-4">
                                            <div className="w-10 h-10 bg-gray-50 rounded-xl overflow-hidden border border-gray-100 flex items-center justify-center text-gray-300">
                                                {item.photo ? <img src={item.photo} alt={item.name} className="w-full h-full object-cover" /> : <Package size={18} />}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="space-y-0.5">
                                                <p className="font-bold text-sm text-gray-900 group-hover:text-blue-600 transition-colors">{item.name}</p>
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">SKU: {item.sku}</p>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className={cn(
                                                "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border-0 font-bold text-[10px] uppercase tracking-wider",
                                                colorStyles.bg,
                                                colorStyles.text
                                            )}>
                                                <IconComp size={12} strokeWidth={2.5} />
                                                {item.category?.name || 'Uncategorized'}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1.5 text-xs font-bold text-gray-600">
                                                <MapPin size={12} className="text-gray-300" />
                                                {item.location?.name || 'No Location'}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <div className="flex flex-col items-center">
                                                <span className="font-bold text-sm text-gray-900">{item.currentStock}</span>
                                                {item.currentStock === 0 ? (
                                                    <span className="text-[9px] font-black text-red-500 uppercase tracking-tighter">Out of Stock</span>
                                                ) : item.currentStock <= (item.lowStockThreshold || 20) && (
                                                    <span className="text-[9px] font-black text-red-500 uppercase tracking-tighter">Low Stock</span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center font-bold text-sm text-gray-900">
                                            {item.issuedCount || 0}
                                        </TableCell>
                                        <TableCell className="text-right font-black text-sm text-gray-900">
                                            ${parseFloat(item.costPerUnit || 0).toFixed(2)}
                                        </TableCell>
                                        <TableCell className="text-right pr-6">
                                            <div className="flex items-center justify-end gap-1.5">
                                                <Button
                                                    variant="outline"
                                                    size="icon"
                                                    className="w-8 h-8 rounded-lg border-gray-100 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100 transition-all shadow-sm"
                                                    onClick={() => { setSelectedItem(item); setIsPreviewModalOpen(true); }}
                                                    title="View Details"
                                                >
                                                    <Eye size={14} />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="icon"
                                                    className="w-8 h-8 rounded-lg border-gray-100 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-100 transition-all shadow-sm"
                                                    onClick={() => { setSelectedItem(item); setIsAddModalOpen(true); }}
                                                    title="Edit Item"
                                                >
                                                    <Edit size={14} />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="icon"
                                                    className="w-8 h-8 rounded-lg border-gray-100 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100 transition-all shadow-sm"
                                                    onClick={() => { setSelectedItem(item); setIsTransferModalOpen(true); }}
                                                    title="Transfer Stock"
                                                    disabled={item.currentStock <= 0}
                                                >
                                                    <ArrowRightLeft size={14} />
                                                </Button>
                                                <Can I={ACTIONS.CYCLE} a={RESOURCES.INVENTORY_ITEM}>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="w-8 h-8 rounded-lg border-gray-100 hover:bg-slate-50 hover:text-slate-600 hover:border-slate-100 transition-all shadow-sm"
                                                        onClick={() => { setSelectedItem(item); setIsCycleModalOpen(true); }}
                                                        title="Stock Cycling"
                                                    >
                                                        <RefreshCcw size={14} />
                                                    </Button>
                                                </Can>
                                                {item.status === 'Archived' ? (
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="w-8 h-8 rounded-lg border-gray-100 hover:bg-green-50 hover:text-green-600 hover:border-green-100 transition-all shadow-sm"
                                                        onClick={() => handleRestoreItem(item)}
                                                        title="Restore Item"
                                                    >
                                                        <RotateCcw size={14} />
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className={cn(
                                                            "w-8 h-8 rounded-lg border-gray-100 transition-all shadow-sm",
                                                            (item.issuedCount > 0 || item.incomingCount > 0)
                                                                ? "hover:bg-amber-50 hover:text-amber-600 hover:border-amber-100"
                                                                : "hover:bg-red-50 hover:text-red-600 hover:border-red-100"
                                                        )}
                                                        onClick={() => { setSelectedItem(item); setIsDeleteModalOpen(true); }}
                                                        title={(item.issuedCount > 0 || item.incomingCount > 0) ? "Archive Item" : "Delete Item"}
                                                    >
                                                        {(item.issuedCount > 0 || item.incomingCount > 0) ? <Archive size={14} /> : <Trash2 size={14} />}
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4">
                {loading ? (
                    [...Array(3)].map((_, i) => (
                        <div key={i} className="bg-white p-5 rounded-[32px] border border-gray-100 animate-pulse space-y-5">
                            <div className="flex items-center gap-4">
                                <div className="w-20 h-20 bg-gray-50 rounded-[24px]" />
                                <div className="space-y-3 flex-1">
                                    <div className="h-5 bg-gray-50 rounded-full w-3/4" />
                                    <div className="h-3 bg-gray-50 rounded-full w-1/2" />
                                    <div className="h-6 bg-gray-50 rounded-lg w-1/3" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="h-16 bg-gray-50 rounded-2xl" />
                                <div className="h-16 bg-gray-50 rounded-2xl" />
                            </div>
                            <div className="h-11 bg-gray-50 rounded-2xl" />
                        </div>
                    ))
                ) : items.length === 0 ? (
                    <div className="bg-white rounded-[32px] border border-gray-100 p-16 text-center shadow-sm">
                        <div className="flex flex-col items-center justify-center">
                            <div className="w-24 h-24 bg-gray-50 rounded-[40px] flex items-center justify-center mb-6">
                                <Package size={40} className="text-gray-200" />
                            </div>
                            <h3 className="text-xl font-black text-gray-900 mb-2">Inventory Empty</h3>
                            <p className="text-sm font-medium text-gray-400 max-w-[240px] leading-relaxed">
                                No items found matching your current filter criteria.
                            </p>
                        </div>
                    </div>
                ) : (
                    items.map((item) => {
                        const colorStyles = COLOR_MAP[item.category?.color || 'blue'] || COLOR_MAP.blue;
                        const IconComp = ICON_MAP[item.category?.icon as string] || Tag;
                        const isLowStock = item.currentStock <= (item.lowStockThreshold || 20) && item.currentStock > 0;
                        const isOutStock = item.currentStock === 0;

                        return (
                            <div key={item.id} className="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden group">
                                <div className="p-5 space-y-5">
                                    {/* Top Section: Photo & Identity */}
                                    <div className="flex gap-4">
                                        <div className="w-20 h-20 bg-gray-50 rounded-[24px] overflow-hidden border border-gray-100 flex items-center justify-center text-gray-300 shrink-0 shadow-inner">
                                            {item.photo ? (
                                                <img src={item.photo} alt={item.name} className="w-full h-full object-cover transition-transform group-hover:scale-110 duration-500" />
                                            ) : (
                                                <Package size={28} strokeWidth={1.5} />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0 flex flex-col justify-center">
                                            <div className="flex items-start justify-between gap-2">
                                                <h3 className="font-black text-gray-900 text-lg leading-tight truncate">{item.name}</h3>
                                                <div className="text-right shrink-0">
                                                    <p className="font-black text-blue-600 leading-none">${parseFloat(item.costPerUnit || 0).toFixed(2)}</p>
                                                    <p className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-1">Per Unit</p>
                                                </div>
                                            </div>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mt-1">SKU: {item.sku}</p>
                                            <div className={cn(
                                                "inline-flex items-center gap-1.5 px-3 py-1 mt-2.5 rounded-lg border-0 font-bold text-[9px] uppercase tracking-wider self-start mr-auto",
                                                colorStyles.bg,
                                                colorStyles.text
                                            )}>
                                                <IconComp size={10} strokeWidth={3} />
                                                {item.category?.name || 'General'}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Stats Grid */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className={cn(
                                            "p-3.5 rounded-2xl border transition-colors",
                                            isOutStock ? "bg-red-50/50 border-red-100" :
                                                isLowStock ? "bg-amber-50/50 border-amber-100" :
                                                    "bg-gray-50/50 border-gray-50"
                                        )}>
                                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-2">Available Stock</p>
                                            <div className="flex items-baseline gap-1.5">
                                                <span className={cn(
                                                    "text-xl font-black",
                                                    isOutStock ? "text-red-600" :
                                                        isLowStock ? "text-amber-600" :
                                                            "text-gray-900"
                                                )}>{item.currentStock}</span>
                                                <span className="text-[10px] font-bold text-gray-400">Units</span>
                                            </div>
                                        </div>
                                        <div className="p-3.5 rounded-2xl bg-gray-50/50 border border-gray-50">
                                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-2">Total Issued</p>
                                            <div className="flex items-baseline gap-1.5">
                                                <span className="text-xl font-black text-gray-900">{item.issuedCount || 0}</span>
                                                <span className="text-[10px] font-bold text-gray-400">Claims</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Location Footnote */}
                                    <div className="flex items-center gap-2 px-1 text-gray-500">
                                        <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center text-blue-500">
                                            <MapPin size={12} />
                                        </div>
                                        <p className="text-xs font-bold whitespace-nowrap overflow-hidden text-ellipsis">
                                            Stored at <span className="text-gray-900">{item.location?.name || 'Global Warehouse'}</span>
                                        </p>
                                    </div>
                                </div>

                                {/* Action Toolbar */}
                                <div className="p-3 bg-gray-50/50 border-t border-gray-50 flex items-center gap-2 overflow-x-auto no-scrollbar">
                                    <Button
                                        variant="outline"
                                        className="h-11 flex-1 rounded-2xl border-white bg-white hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100 shadow-sm gap-2 font-bold transition-all shrink-0"
                                        onClick={() => { setSelectedItem(item); setIsPreviewModalOpen(true); }}
                                    >
                                        <Eye size={16} />
                                        <span className="text-xs">View</span>
                                    </Button>

                                    <Button
                                        variant="outline"
                                        className="h-11 flex-1 rounded-2xl border-white bg-white hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100 shadow-sm gap-2 font-bold transition-all shrink-0"
                                        onClick={() => { setSelectedItem(item); setIsTransferModalOpen(true); }}
                                        disabled={item.currentStock <= 0}
                                    >
                                        <ArrowRightLeft size={16} />
                                        <span className="text-xs">Move</span>
                                    </Button>

                                    <Can I={ACTIONS.CYCLE} a={RESOURCES.INVENTORY_ITEM}>
                                        <Button
                                            variant="outline"
                                            className="h-11 flex-1 rounded-2xl border-white bg-white hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100 shadow-sm gap-2 font-bold transition-all shrink-0"
                                            onClick={() => { setSelectedItem(item); setIsCycleModalOpen(true); }}
                                        >
                                            <RefreshCcw size={16} />
                                            <span className="text-xs">Cycle</span>
                                        </Button>
                                    </Can>

                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className={cn(
                                            "h-11 w-11 rounded-2xl border-white bg-white shadow-sm shrink-0 transition-all",
                                            item.status === 'Archived' ? "hover:bg-green-50 hover:text-green-600 hover:border-green-100" :
                                                (item.issuedCount > 0 || item.incomingCount > 0)
                                                    ? "hover:bg-amber-50 hover:text-amber-600 hover:border-amber-100"
                                                    : "hover:bg-red-50 hover:text-red-600 hover:border-red-100"
                                        )}
                                        onClick={() => {
                                            setSelectedItem(item);
                                            item.status === 'Archived' ? handleRestoreItem(item) : setIsDeleteModalOpen(true);
                                        }}
                                    >
                                        {item.status === 'Archived' ? <RotateCcw size={16} /> :
                                            (item.issuedCount > 0 || item.incomingCount > 0) ? <Archive size={16} /> : <Trash2 size={16} />}
                                    </Button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Modals */}
            {isAddModalOpen && (
                <AddItemModal
                    isOpen={isAddModalOpen}
                    onClose={() => { setIsAddModalOpen(false); setSelectedItem(null); }}
                    onRefresh={fetchData}
                    categories={categories}
                    locations={locations}
                    itemToEdit={selectedItem}
                />
            )}
            {isAddSupplierOpen && (
                <AddSupplierModal
                    isOpen={isAddSupplierOpen}
                    onClose={() => setIsAddSupplierOpen(false)}
                    onRefresh={fetchMeta}
                />
            )}
            {isPreviewModalOpen && selectedItem && (
                <PreviewItemModal
                    isOpen={isPreviewModalOpen}
                    onClose={() => { setIsPreviewModalOpen(false); setSelectedItem(null); }}
                    item={selectedItem}
                />
            )}
            {isTransferModalOpen && selectedItem && (
                <TransferStockModal
                    isOpen={isTransferModalOpen}
                    onClose={() => { setIsTransferModalOpen(false); setSelectedItem(null); }}
                    onRefresh={fetchData}
                    item={selectedItem}
                    locations={locations}
                />
            )}
            {isCycleModalOpen && selectedItem && (
                <StockCyclingModal
                    isOpen={isCycleModalOpen}
                    onClose={() => { setIsCycleModalOpen(false); setSelectedItem(null); }}
                    onRefresh={fetchData}
                    item={selectedItem}
                />
            )}

            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteItem}
                title={((selectedItem?.issuedCount || 0) > 0 || (selectedItem?.incomingCount || 0) > 0) ? "Archive Inventory Item" : "Delete Inventory Item"}
                message={((selectedItem?.issuedCount || 0) > 0 || (selectedItem?.incomingCount || 0) > 0)
                    ? `This item has existing issuances or incoming orders. It will be archived instead of deleted to preserve historical data.`
                    : `Are you sure you want to delete "${selectedItem?.name}"? This action cannot be undone and will remove all stock and history records.`}
                actionType={((selectedItem?.issuedCount || 0) > 0 || (selectedItem?.incomingCount || 0) > 0) ? "archive" : "delete"}
            />
        </div>
    );
};

// --- Stock Transfer Modal ---
interface TransferStockModalProps {
    isOpen: boolean;
    onClose: () => void;
    onRefresh: () => void;
    item: any;
    locations: any[];
}

const TransferStockModal: React.FC<TransferStockModalProps> = ({ isOpen, onClose, onRefresh, item, locations }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        targetLocationId: '',
        quantity: 1,
        notes: ''
    });

    const handleSubmit = async () => {
        if (!formData.targetLocationId) {
            toast.error('Please select a target location');
            return;
        }
        if (formData.quantity <= 0 || formData.quantity > item.currentStock) {
            toast.error(`Please enter a valid quantity (1 - ${item.currentStock})`);
            return;
        }

        try {
            setLoading(true);
            await api.post(`/api/inventory/items/${item.id}/transfer`, formData);
            toast.success('Stock transferred successfully');
            onRefresh();
            onClose();
        } catch (err: any) {
            toast.error(err.message || 'Failed to transfer stock');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md rounded-[32px] p-0 overflow-hidden border-0 shadow-2xl max-h-[95vh] flex flex-col">
                <div className="p-6 sm:p-8 space-y-8 bg-white flex-1 overflow-y-auto custom-scrollbar">
                    <div className="space-y-2">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                                <ArrowRightLeft className="text-blue-500" size={20} />
                            </div>
                            <h2 className="text-2xl font-black text-gray-900 tracking-tight">Transfer Stock</h2>
                        </div>
                        <p className="text-sm font-medium text-gray-500 leading-relaxed">
                            Move items from <span className="text-gray-900 font-bold">{item.location?.name || 'Current Location'}</span> to another facility.
                        </p>
                    </div>

                    <div className="space-y-6">
                        {/* Item Summary */}
                        <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-xl border border-gray-100 flex items-center justify-center text-gray-300">
                                {item.photo ? <img src={item.photo} alt={item.name} className="w-full h-full object-cover rounded-xl" /> : <Package size={20} />}
                            </div>
                            <div>
                                <p className="font-bold text-gray-900 text-sm">{item.name}</p>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Available: {item.currentStock} Units</p>
                            </div>
                        </div>

                        {/* Destination */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Destination Location</label>
                            <Select
                                value={formData.targetLocationId}
                                onValueChange={(val) => setFormData({ ...formData, targetLocationId: val })}
                            >
                                <SelectTrigger className="h-12 rounded-2xl border-gray-100 bg-gray-50/50 focus:ring-4 focus:ring-blue-500/5 font-bold transition-all">
                                    <SelectValue placeholder="Select Destination" />
                                </SelectTrigger>
                                <SelectContent className="rounded-2xl border-gray-100 shadow-2xl">
                                    {locations.filter(l => l.id !== item.location?.id).map((loc) => (
                                        <SelectItem key={loc.id} value={loc.id} className="font-bold py-3 px-4 focus:bg-blue-50 focus:text-blue-600 rounded-xl mx-1 my-1 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <MapPin size={14} className="opacity-40" />
                                                <span>{loc.name}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Quantity */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Quantity to Move</label>
                            <div className="relative group">
                                <Input
                                    type="number"
                                    min={1}
                                    max={item.currentStock}
                                    className="h-14 pl-5 pr-12 rounded-2xl border-gray-100 bg-gray-50/50 focus:ring-4 focus:ring-blue-500/5 font-black text-xl shadow-inner transition-all"
                                    value={formData.quantity}
                                    onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                                />
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[9px] font-black text-gray-300 bg-white px-2 py-1 rounded-lg border border-gray-50 uppercase tracking-widest group-focus-within:text-blue-500 transition-colors pointer-events-none">
                                    Units
                                </div>
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Reason / Notes (Optional)</label>
                            <textarea
                                className="w-full h-24 rounded-2xl border-none bg-gray-50/50 p-4 text-sm focus:outline-none focus:ring-4 focus:ring-blue-500/5 transition-all font-medium placeholder:text-gray-300 shadow-inner"
                                placeholder="State the purpose of this reallocation..."
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            />
                        </div>
                    </div>
                </div>

                <div className="p-6 bg-gray-50 border-t border-gray-100 flex gap-3 shrink-0">
                    <Button
                        variant="ghost"
                        onClick={onClose}
                        className="flex-1 rounded-2xl h-14 font-black text-[11px] uppercase tracking-widest text-gray-400 hover:text-gray-900 transition-all"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || !formData.targetLocationId || formData.quantity <= 0}
                        className="flex-[2] bg-blue-600 hover:bg-blue-700 text-white rounded-2xl h-14 font-black text-[11px] uppercase tracking-widest shadow-2xl shadow-blue-500/20 transition-all gap-2 disabled:opacity-50 disabled:shadow-none active:scale-[0.98]"
                    >
                        {loading ? (
                            <Loader2 size={18} className="animate-spin" />
                        ) : (
                            <>
                                <ArrowRightLeft size={18} />
                                Confirm Transfer
                            </>
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ManageItems;
